export const SEPARATOR = "\u00a0\u00bb\u00a0";

const MODEL_EVENT = "update:modelValue";
const OPEN_EVENT = "update:open";
const SELECTED_ID = "easy_notification_mail_selected";
const ONLY_MY_EVENTS_ID = "easy_notification_mail_only_my_events";
const PROJECT_IDS_ID = "easy_notification_project_ids";
const TREE_ID = "easy_notification_projects_tree";
const DIALOG_ID = "easy_notification_projects_dialog";
const PICKER_TEMPLATE_ID = "easy_notification_projects_picker_template";
const PICKER_CONTAINER_ID = "easy_notification_projects_picker_container";
const PICKER_SELECTOR = "easy8-autocomplete[data-id='easy_notification_projects_picker']";
const PICKER_VALUE_SELECTOR = "script[data-prop='value']";
const EMPTY_ID = "easy_notification_projects_empty";
const PROJECT_IDS_NAME = "user[notified_project_ids][]";

/** @typedef {{ value: string | number, label: string }} PickerItem */
/** @typedef {{ name: string, id?: string | number, selected: boolean, children: TreeNode[] }} TreeNode */

/** @type {PickerItem[] | null} */
let latestSelection = null;
/** @type {PickerItem[]} */
let appliedSelection = [];

/** @param {Event} event */
const modelValue = event => /** @type {CustomEvent} */ (event).detail?.[0];

/**
 * @param {Document | Element} root
 * @returns {Element | null}
 */
export const bindMailMode = (root = document) => {
  const select = root.querySelector("easy8-ds-select[data-id='user_mail_notification']");
  if (!select) return null;

  select.addEventListener(MODEL_EVENT, event => {
    const value = modelValue(event);
    const mode = typeof value === "object" && value !== null ? value.value : value;
    const selectedSection = root.querySelector(`#${SELECTED_ID}`);
    const onlyMyEventsSection = root.querySelector(`#${ONLY_MY_EVENTS_ID}`);
    if (selectedSection) /** @type {HTMLElement} */ (selectedSection).hidden = mode !== "selected";
    if (onlyMyEventsSection) /** @type {HTMLElement} */ (onlyMyEventsSection).hidden = mode !== "only_my_events";
  });
  return select;
};

/**
 * @param {Document | Element} root
 * @returns {Element | null}
 */
export const bindProjectsPicker = (root = document) => {
  latestSelection = null;
  const dialog = root.querySelector(`#${DIALOG_ID} easy8-ds-dialog`);
  const template = /** @type {HTMLTemplateElement | null} */ (root.querySelector(`#${PICKER_TEMPLATE_ID}`));
  if (!dialog || !template) return null;

  const value = template.content.querySelector(`${PICKER_SELECTOR} > ${PICKER_VALUE_SELECTOR}`)?.textContent;
  appliedSelection = value ? JSON.parse(value) : [];
  dialog.addEventListener(MODEL_EVENT, event => {
    if (/** @type {Element} */ (event.target).matches(PICKER_SELECTOR)) latestSelection = modelValue(event) || [];
  });
  dialog.addEventListener(OPEN_EVENT, event => {
    if (event.target === dialog) dialog.setAttribute("data-open", String(modelValue(event)));
  });
  return dialog;
};

export const openProjectsDialog = () => {
  const dialog = document.querySelector(`#${DIALOG_ID} easy8-ds-dialog`);
  const template = /** @type {HTMLTemplateElement | null} */ (document.getElementById(PICKER_TEMPLATE_ID));
  const container = document.getElementById(PICKER_CONTAINER_ID);
  if (!dialog || !template || !container) return;

  const fragment = document.importNode(template.content, true);
  const picker = fragment.querySelector(PICKER_SELECTOR);
  if (picker) {
    let script = picker.querySelector(`:scope > ${PICKER_VALUE_SELECTOR}`);
    if (!script) {
      script = document.createElement("script");
      script.setAttribute("type", "application/json");
      script.setAttribute("data-prop", "value");
      picker.appendChild(script);
    }
    script.textContent = JSON.stringify(appliedSelection);
  }
  latestSelection = null;
  container.replaceChildren(fragment);
  dialog.setAttribute("data-open", "true");
};

/**
 * @param {PickerItem[]} items
 * @param {string} separator
 * @returns {{ nodes: TreeNode[] }}
 */
export const buildTree = (items, separator) => {
  /** @type {TreeNode[]} */
  const nodes = [];
  items.forEach(item => {
    let level = nodes;
    /** @type {TreeNode | undefined} */
    let node;
    const names = item.label.split(separator);
    names.forEach((name, index) => {
      const leaf = index === names.length - 1;
      node = level.find(candidate =>
        leaf
          ? candidate.id === item.value || (candidate.id === undefined && candidate.name === name)
          : candidate.name === name
      );
      if (!node) {
        node = { name, selected: false, children: [] };
        level.push(node);
      }
      if (leaf) {
        node.id = item.value;
        node.selected = true;
      }
      level = node.children;
    });
  });
  return { nodes };
};

/**
 * @param {TreeNode[]} nodes
 * @param {number} limit
 * @returns {{ visible: Set<TreeNode>, total: number }}
 */
const visibleNodes = (nodes, limit) => {
  const visible = new Set();
  let total = 0;
  /**
   * @param {TreeNode[]} level
   * @param {TreeNode[]} ancestors
   */
  const walk = (level, ancestors) => {
    level.forEach(node => {
      if (node.selected) {
        total += 1;
        if (total <= limit) [...ancestors, node].forEach(shown => visible.add(shown));
      }
      walk(node.children, [...ancestors, node]);
    });
  };
  walk(nodes, []);
  return { visible, total };
};

/**
 * Same markup as ApplicationHelper#render_project_nested_lists, which renders the tree on the server.
 * @param {TreeNode[]} nodes
 * @param {Set<TreeNode>} visible
 * @param {boolean} root
 * @returns {HTMLUListElement | null}
 */
const renderList = (nodes, visible, root = true) => {
  const shown = nodes.filter(node => visible.has(node));
  if (!shown.length) return null;

  const level = root ? "root" : "child";
  const list = document.createElement("ul");
  list.className = root ? "projects root" : "projects";
  shown.forEach(node => {
    const item = document.createElement("li");
    item.className = level;
    const wrapper = document.createElement("div");
    wrapper.className = level;
    const name = document.createElement("span");
    name.className = "name";
    name.textContent = node.name;
    if (node.id !== undefined) name.dataset.projectId = String(node.id);
    name.dataset.selected = String(node.selected);
    wrapper.appendChild(name);
    item.appendChild(wrapper);
    const children = renderList(node.children, visible, false);
    if (children) item.appendChild(children);
    list.appendChild(item);
  });
  return list;
};

/**
 * @param {PickerItem[]} items
 */
export const renderTree = items => {
  const section = document.getElementById(SELECTED_ID);
  const container = document.getElementById(TREE_ID);
  if (!section || !container) return;

  if (!items.length) {
    const empty = document.createElement("p");
    empty.id = EMPTY_ID;
    empty.textContent = section.dataset.emptyLabel || "";
    container.replaceChildren(empty);
    return;
  }

  const limit = Number(section.dataset.limit) || Infinity;
  const { nodes } = buildTree(items, section.dataset.separator || SEPARATOR);
  const { visible, total } = visibleNodes(nodes, limit);
  container.replaceChildren();

  const tree = renderList(nodes, visible);
  if (tree) container.appendChild(tree);
  if (total <= limit) return;

  const shown = Math.min(limit, total);
  const more = document.createElement("li");
  more.className = "more";
  more.dataset.count = String(total - shown);
  more.textContent = (section.dataset.moreLabel || "")
    .replace("%{shown}", String(shown))
    .replace("%{total}", String(total));
  const moreList = document.createElement("ul");
  moreList.className = "projects";
  moreList.appendChild(more);
  container.appendChild(moreList);
};

/**
 * @param {HTMLElement} button
 */
export const applyProjects = button => {
  const dialog = /** @type {(HTMLElement & { close?: () => void }) | null} */ (button.closest("easy8-ds-dialog"));
  const selection = latestSelection;
  const ids = document.getElementById(PROJECT_IDS_ID);
  if (selection && ids) {
    const inputs = ["", ...selection.map(item => String(item.value))].map(value => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = PROJECT_IDS_NAME;
      input.value = value;
      return input;
    });
    ids.replaceChildren(...inputs);
    appliedSelection = selection;
    renderTree(selection);
  }
  dialog?.close?.();
};
